// Finance ▸ Live numbers (CEO 2026-10-09) — the first full pull from
// QuickBooks: P&L by month since Jan 2025, the balance sheet at the latest
// month end, who owes us / who we owe, payroll by month, the watched clean-up
// accounts, and journal entries since May 31 with the COGS accrual check.
// Read-only; every figure is QuickBooks' own.
import { useState } from "react";
import { cn } from "@/lib/utils";
import { FinanceHeader } from "./FinanceNav";
import { LiveGate, LiveToolbar, Panel, focusRing, money, pct, shortDate, useLiveFinance, type AgingView, type LiveFinance } from "./shared";

const JUMPS = [
  ["pnl", "P&L by month"],
  ["bs", "Balance sheet"],
  ["aging", "Owed to us / we owe"],
  ["payroll", "Payroll"],
  ["registers", "Watched accounts"],
  ["journals", "Journal entries"],
] as const;

export function LiveNumbersPage() {
  const { data, error, refreshing, reload } = useLiveFinance();
  const live = data?.connected ? data : null;
  return (
    <div className="mx-auto w-full max-w-[1180px] px-6 py-bay md:px-10">
      <FinanceHeader title="Live numbers" intro="Straight from QuickBooks, nothing re-keyed. Use these to verify the open items and to rebuild the health check." />
      <LiveGate data={data} error={error} onRetry={reload} />
      {live ? (
        <>
          <LiveToolbar data={live} refreshing={refreshing} onRefresh={reload} />
          <nav aria-label="On this page" className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-meta">
            {JUMPS.map(([id, label]) => <a key={id} href={`#${id}`} className={cn("underline underline-offset-4 hover:opacity-70", focusRing)}>{label}</a>)}
          </nav>
          <PnlPanel live={live} />
          <BalanceSheetPanel live={live} />
          <div id="aging" className="grid gap-x-gutter md:grid-cols-2">
            <AgingPanel title="Owed to us (A/R aging)" aging={live.arAging!} />
            <AgingPanel title="We owe (A/P aging)" aging={live.apAging!} />
          </div>
          <PayrollPanel live={live} />
          <RegistersPanel live={live} />
          <JournalsPanel live={live} />
        </>
      ) : null}
    </div>
  );
}

function PnlPanel({ live }: { live: LiveFinance }) {
  const months = live.pnl!.months;
  const rows: Array<[string, (m: (typeof months)[number]) => string, boolean?]> = [
    ["Revenue", (m) => money(m.income)],
    ["Cost of goods", (m) => money(m.cogs)],
    ["Gross profit", (m) => money(m.grossProfit), true],
    ["Gross margin", (m) => (m.income ? pct(((m.grossProfit ?? 0) / m.income) * 100) : "—")],
    ["Payroll", (m) => money(m.payroll)],
    ["Other expenses", (m) => money(m.expenses == null ? null : m.expenses - (m.payroll ?? 0))],
    ["Net income", (m) => money(m.netIncome), true],
  ];
  return (
    <Panel id="pnl" title="P&L by month" aside={`Jan 2025 – ${months.at(-1)?.month ?? "—"} · accrual`}>
      <div className="overflow-x-auto">
        <table className="w-full text-right text-meta tabular-nums">
          <caption className="sr-only">Profit and loss by month</caption>
          <thead className="text-ink-muted">
            <tr>
              <th scope="col" className="sticky left-0 bg-white py-2 pr-4 text-left font-semibold">&nbsp;</th>
              {months.map((m) => <th key={m.month} scope="col" className="whitespace-nowrap px-2 py-2 font-semibold">{m.month}{m.partial ? " *" : ""}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, fn, strong]) => (
              <tr key={label} className="border-t border-rule">
                <th scope="row" className={cn("sticky left-0 whitespace-nowrap bg-white py-2 pr-4 text-left", strong ? "font-semibold" : "font-normal")}>{label}</th>
                {months.map((m) => <td key={m.month} className={cn("whitespace-nowrap px-2 py-2", strong && "font-semibold")}>{fn(m)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-meta text-ink-muted">* this month so far. Monthly margins swing with supplier-bill timing until each month's COGS accrual is booked (open item 12).</p>
    </Panel>
  );
}

function BalanceSheetPanel({ live }: { live: LiveFinance }) {
  const bs = live.balanceSheet!;
  const [all, setAll] = useState(false);
  const lines = all ? bs.lines : bs.lines.filter((l) => l.depth <= 3 || l.kind === "total");
  return (
    <Panel id="bs" title={`Balance sheet at ${shortDate(bs.asOf)}`} aside={<button type="button" className="btn-secondary text-meta" onClick={() => setAll((v) => !v)}>{all ? "Fewer lines" : "Every line"}</button>}>
      <table className="w-full text-meta tabular-nums">
        <caption className="sr-only">Balance sheet</caption>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className={cn(l.kind === "total" && "border-t border-rule")}>
              <th scope="row" className={cn("py-1 text-left", l.kind === "data" ? "font-normal" : "font-semibold")} style={{ paddingLeft: `${l.depth * 16}px` }}>{l.name}</th>
              <td className={cn("py-1 text-right", l.kind === "total" && "font-semibold")}>{l.kind === "header" ? "" : money(l.amount, true)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function AgingPanel({ title, aging }: { title: string; aging: AgingView }) {
  const [all, setAll] = useState(false);
  const totalIdx = aging.columns.findIndex((c) => c.toLowerCase() === "total");
  const sorted = [...aging.rows].sort((a, b) => (b.values[totalIdx] ?? 0) - (a.values[totalIdx] ?? 0));
  const rows = all ? sorted : sorted.slice(0, 10);
  return (
    <Panel title={title} aside={sorted.length > 10 ? <button type="button" className="btn-secondary text-meta" onClick={() => setAll((v) => !v)}>{all ? "Top 10" : `All ${sorted.length}`}</button> : `${sorted.length} names`}>
      <div className="overflow-x-auto">
        <table className="w-full text-right text-meta tabular-nums">
          <caption className="sr-only">{title}</caption>
          <thead className="text-ink-muted">
            <tr><th scope="col" className="py-2 pr-3 text-left font-semibold">Name</th>{aging.columns.map((c) => <th key={c} scope="col" className="whitespace-nowrap px-2 py-2 font-semibold">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-rule">
                <th scope="row" className="py-1.5 pr-3 text-left font-normal">{r.name}</th>
                {r.values.map((v, i) => <td key={i} className={cn("px-2 py-1.5", i === totalIdx && "font-semibold", (v ?? 0) < 0 && "text-risk")}>{v == null || v === 0 ? "" : money(v)}</td>)}
              </tr>
            ))}
            <tr className="border-t-2 border-ink">
              <th scope="row" className="py-1.5 pr-3 text-left font-semibold">Total</th>
              {aging.totals.map((v, i) => <td key={i} className="px-2 py-1.5 font-semibold">{money(v)}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function PayrollPanel({ live }: { live: LiveFinance }) {
  const p = live.payroll!;
  const year = live.today!.slice(0, 4);
  const ytd = p.months.filter((m) => m.month.endsWith(year)).reduce((s, m) => s + (m.amount ?? 0), 0);
  const max = Math.max(...p.months.map((m) => m.amount ?? 0), 1);
  return (
    <Panel id="payroll" title="Payroll by month" aside={`${year} to date ${money(ytd)}`}>
      <div className="flex h-36 items-stretch gap-1" aria-hidden>
        {p.months.map((m) => (
          <div key={m.month} className="flex flex-1 flex-col justify-end" title={`${m.month}: ${money(m.amount)}`}>
            <div className={cn("w-full rounded-t-sm", m.partial ? "bg-rule" : "bg-ink")} style={{ height: `${((m.amount ?? 0) / max) * 100}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-meta text-ink-muted" aria-hidden><span>{p.months[0]?.month}</span><span>{p.months.at(-1)?.month}</span></div>
      <table className="mt-3 w-full text-meta tabular-nums">
        <caption className="sr-only">Payroll accounts, total since January 2025</caption>
        <tbody>
          {p.lines.map((l) => <tr key={l.name} className="border-t border-rule"><th scope="row" className="py-1.5 text-left font-normal">{l.name}</th><td className="py-1.5 text-right">{money(l.total)}</td></tr>)}
        </tbody>
      </table>
      <p className="mt-2 text-meta text-ink-muted">{p.note}</p>
    </Panel>
  );
}

function RegistersPanel({ live }: { live: LiveFinance }) {
  return (
    <Panel id="registers" title="Watched accounts" aside="Clean-up accounts that should end at $0 (Trionfo near it)">
      <div className="grid gap-4 md:grid-cols-2">
        {live.registers!.map((r) => (
          <div key={r.key} className="rounded border border-rule p-4">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-label font-semibold">{r.name ?? r.label}</p>
              <p className={cn("text-label font-semibold tabular-nums", r.balance != null && Math.abs(r.balance) >= 0.005 && "text-risk")}>{r.found ? money(r.balance, true) : "Not found"}</p>
            </div>
            <p className="text-meta text-ink-muted">{r.found ? `${r.accountType ?? ""}${r.active === false ? " · inactive" : ""} · ${r.entries} entries since 2024` : "No account by this name in QuickBooks."}</p>
            {r.recent.length ? (
              <table className="mt-2 w-full text-meta tabular-nums">
                <caption className="sr-only">Latest entries in {r.label}</caption>
                <tbody>
                  {r.recent.slice(0, 6).map((e, i) => (
                    <tr key={i} className="border-t border-rule">
                      <td className="whitespace-nowrap py-1 pr-2 text-ink-muted">{e.date}</td>
                      <td className="py-1 pr-2">{e.name || e.type}{e.memo ? <span className="text-ink-muted"> · {e.memo}</span> : null}</td>
                      <td className="whitespace-nowrap py-1 text-right">{money(e.amount, true)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        ))}
      </div>
    </Panel>
  );
}

const ACCRUAL_WORD = { ok: ["Booked + reversed", "bg-ok-bg text-ok"], not_reversed: ["Not reversed", "bg-risk-bg text-risk"], missing: ["No accrual", "bg-risk-bg text-risk"] } as const;

function JournalsPanel({ live }: { live: LiveFinance }) {
  const c = live.cogsAccrual!;
  return (
    <Panel id="journals" title="Journal entries since May 31" aside={`Accrued Supplier Costs: ${c.accountFound ? money(c.balance, true) : "account not found"}`}>
      <p className="text-label font-semibold">COGS accrual, month by month</p>
      <table className="mt-2 w-full text-meta tabular-nums">
        <caption className="sr-only">COGS accrual check by month end</caption>
        <thead className="text-ink-muted"><tr><th scope="col" className="py-1.5 text-left font-semibold">Month end</th><th scope="col" className="py-1.5 text-left font-semibold">Accrual</th><th scope="col" className="py-1.5 text-left font-semibold">Reversal (1st)</th><th scope="col" className="py-1.5 text-left font-semibold">Result</th></tr></thead>
        <tbody>
          {c.months.map((m) => (
            <tr key={m.monthEnd} className="border-t border-rule">
              <td className="py-1.5">{shortDate(m.monthEnd)}</td>
              <td className="py-1.5">{m.accrual ? `JE ${m.accrual.docNumber} · ${money(m.accrual.amount, true)}` : "—"}</td>
              <td className="py-1.5">{m.reversal ? `JE ${m.reversal.docNumber} · ${money(m.reversal.amount, true)}` : m.reversalDue ? "—" : "not due yet"}</td>
              <td className="py-1.5"><span className={cn("rounded px-1.5 font-semibold", ACCRUAL_WORD[m.state][1])}>{ACCRUAL_WORD[m.state][0]}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-gutter text-label font-semibold">Every journal entry</p>
      {live.journals!.length === 0 ? <p className="mt-1 text-body text-ink-muted">None since May 31.</p> : (
        <table className="mt-2 w-full text-meta tabular-nums">
          <caption className="sr-only">Journal entries since May 31, newest first</caption>
          <tbody>
            {live.journals!.map((j) => (
              <tr key={j.id} className="border-t border-rule align-top">
                <td className="whitespace-nowrap py-1.5 pr-3">{j.date}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 font-semibold">JE {j.docNumber || j.id}</td>
                <td className="py-1.5 pr-3">{j.lines.map((l, i) => <span key={i} className="block">{l.posting === "Debit" ? "Dr" : "Cr"} {l.account}</span>)}{j.memo ? <span className="block text-ink-muted">{j.memo}</span> : null}</td>
                <td className="whitespace-nowrap py-1.5 text-right">{money(j.total, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
